import { useEffect, useRef, useState } from 'react';
import { Bot, ChevronDown, ChevronRight, Loader2, Send, Sparkles, User } from 'lucide-react';
import { sendAiChat, type AiChatMessage, type AiSqlQuery } from '@/services/aiAnalystService';
import { toastMessage } from '@/utils/toast-message';

interface ChatTurn extends AiChatMessage {
  sql_queries?: AiSqlQuery[];
}

const SUGGESTIONS = [
  'Revenue and order count this month vs last month',
  'Top 5 agents by delivered orders in the last 30 days',
  'Which products have the highest RTO rate since May?',
  'Top customer objections on calls this month',
  'How many abandoned-cart leads from Facebook converted in June?',
];

const AiAnalyst = () => {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, loading]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || loading) return;
    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: q }];
    setTurns(nextTurns);
    setInput('');
    setLoading(true);
    try {
      const history: AiChatMessage[] = nextTurns.map(({ role, content }) => ({ role, content }));
      const res = await sendAiChat(history.slice(-20));
      const d = res?.data;
      if (d?.reply) {
        setTurns([...nextTurns, { role: 'assistant', content: d.reply, sql_queries: d.sql_queries }]);
      } else {
        toastMessage('The analyst returned no answer — try rephrasing.', 'error');
        setTurns(nextTurns.slice(0, -1));
        setInput(q);
      }
    } catch {
      toastMessage('AI analyst request failed', 'error');
      setTurns(nextTurns.slice(0, -1));
      setInput(q);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] max-w-4xl mx-auto px-4">
      <div className="py-4 border-b">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Sparkles size={20} className="text-violet-600" /> AI Analyst
        </h1>
        <p className="text-sm text-gray-500">
          Ask anything about leads, orders, agents, calls or customers. Answers come from the
          analytics warehouse (refreshed every ~3 hours).
        </p>
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {turns.length === 0 && !loading && (
          <div className="mt-8">
            <p className="text-sm text-gray-500 mb-3">Try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  className="text-sm px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-violet-400 hover:text-violet-700 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) =>
          t.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="flex items-start gap-2 max-w-[85%]">
                <div className="bg-violet-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm whitespace-pre-wrap">
                  {t.content}
                </div>
                <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
                  <User size={14} className="text-violet-700" />
                </div>
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="flex items-start gap-2 max-w-[90%]">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot size={14} className="text-gray-600" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-2.5">
                  <div className="text-sm whitespace-pre-wrap">{t.content}</div>
                  {t.sql_queries && t.sql_queries.length > 0 && <SqlDrawer queries={t.sql_queries} />}
                </div>
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 pl-9">
            <Loader2 size={16} className="animate-spin" /> Querying the warehouse…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="py-3 border-t">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask(input);
          }}
          className="flex gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. Which agents had the best conversion last week?"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            disabled={loading}
            maxLength={2000}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-violet-600 text-white rounded-lg px-4 py-2.5 disabled:opacity-40 hover:bg-violet-700 transition-colors"
          >
            <Send size={16} />
          </button>
        </form>
        <p className="text-[11px] text-gray-400 mt-1.5">
          Data is read-only and refreshed every ~3h. Answers can be wrong — expand “SQL used” to verify.
        </p>
      </div>
    </div>
  );
};

const SqlDrawer = ({ queries }: { queries: AiSqlQuery[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 border-t pt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs text-gray-500 hover:text-violet-700 flex items-center gap-1"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        SQL used ({queries.length} {queries.length === 1 ? 'query' : 'queries'})
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {queries.map((q, i) => (
            <div key={i} className="bg-gray-50 rounded-md p-2">
              <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-words font-mono">{q.sql}</pre>
              <div className="text-[10px] text-gray-400 mt-1">
                {q.error ? `error: ${q.error}` : `${q.rows ?? 0} rows`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AiAnalyst;
